/* eslint-disable indent */
import React, { useCallback, useEffect, useState } from 'react';
import DynamicIcon from '@bvt-icons';
import { SelectOption } from '@bvt-shared/component/SelectOption';
import { IconChevron } from '@bvt-assets/icon/icon-chevron';
import { debounce, find } from 'lodash';
import { useAreaSelection } from '@bvt-features/datex/hooks';
import './DatexPoiAdjStepOne.scss';
import ParameterPoi from '@bvt-features/genset/poi/component/ParsetPOI/ParameterPoi';

/**
 * @copyright PT Bhumi Varta Technology
 * @description Datex POI Adjustment Step One Component
 * @author Mochamad Riza Syarif<mochamad.riza@bvarta.com>
 */

/**
 * @typedef IBaseKeyValue
 * @type {Object}
 * @property {string|number} key
 * @property {string} value
 * @property {boolean} disabled
 */

/**
 * @description For Data POI adjustment object
 * @typedef {Object} TPoiAdjDataObject
 * @property {number} province_id
 * @property {string} province_name
 * @property {number} city_id
 * @property {string} city_name
 */

/**
 * @description Data object for POI adjustment
 * @param {object} props
 * @param {TPoiAdjDataObject} props.data
 * @param {(data:TPoiAdjDataObject)=>void} props.onChange
 */

/**
 * @description Province
 * @param {boolean} props.loadingProvince
 * @param {Array<IBaseKeyValue>} props.provinceList
 * @param {(search:string)=>void} props.onSearchProvince
 */
/**
 * @description City
 * @param {boolean} props.loadingCity
 * @param {Array<IBaseKeyValue>} props.cityList
 * @param {(search:string)=>void} props.onSearchCity
 */
/**
 * @description District
 * @param {boolean} props.loadingDistrict
 * @param {Array<IBaseKeyValue>} props.districtList
 * @param {(search:string)=>void} props.onSearchDistrict
 */
/**
 * @description Village
 * @param {boolean} props.loadingVillage
 * @param {Array<IBaseKeyValue>} props.villageList
 * @param {(search:string)=>void} props.onSearchVillage
 */

/**
 * @description PAYLOAD SELECT
 * @typedef PayloadPoiSelect
 * @param {object} checkedState
 * @param {Array<object>} poi_brand
 * @param {Array<object>} poi_criteria
 */

/**
 * @description POI LIST
 * @param {Array<Object>} props.dataListPoi
 * @param {(payload:PayloadPoiSelect) => void} props.onCheckedStateParameterPoi
 * @param {(isValid:boolean)=>void} props.onValidOrInvalid
 */

export const DatexPoiAdjStepOne = ({
  data,
  dataListPoi,
  provinceList,
  cityList,
  districtList,
  villageList,
  loadingProvince,
  loadingCity,
  loadingDistrict,
  loadingVillage,
  onSearchProvince,
  onSearchCity,
  onSearchDistrict,
  onSearchVillage,
  onCheckedStateParameterPoi,
  onValidOrInvalid,
  onChange,
}) => {
  const [openAreaSelection, setOpenAreaSelection] = useState(true);
  const [checkedState, setCheckedState] = useState({});
  const [brands, setBrands] = useState([]);
  const [criterias, setCriterias] = useState([]);
  const [poiGroup, setPoiGroup] = useState({
    total_category: undefined,
    total_brands: undefined,
    total_criteria: undefined,
    poi_group_list: [],
  });

  const handleClickOpenArea = () => {
    setOpenAreaSelection(!openAreaSelection);
  };
  const debouncedSearchProvince = useCallback(debounce(onSearchProvince, 300), [
    onSearchProvince,
  ]);
  const debouncedSearchCity = useCallback(debounce(onSearchCity, 300), [
    onSearchCity,
  ]);
  const debouncedSearchDistrict = useCallback(debounce(onSearchDistrict, 300), [
    onSearchDistrict,
  ]);
  const debouncedSearchVillage = useCallback(debounce(onSearchVillage, 300), [
    onSearchVillage,
  ]);
  const searchFunctions = {
    province: debouncedSearchProvince,
    city: (value) => debouncedSearchCity(value, data?.province_id),
    district: (value) => debouncedSearchDistrict(value, data?.city_id),
    village: (value) => debouncedSearchVillage(value, data?.village_id),
  };
  const {
    renderProvinceDropdown,
    renderCityDropdown,
    renderDistrictDropdown,
    renderVillageDropdown,
    setInputSearch,
  } = useAreaSelection(
    searchFunctions,
    'DatexTelcoStepOneTelco__select--search'
  );

  const groupPoi = () => {
    if (!Array.isArray(brands) || !Array.isArray(criterias)) {
      setPoiGroup({
        poi_list_group: [],
        total_brands: undefined,
        total_category: undefined,
        total_criteria: undefined,
      });
    } else {
      const combinePOI = [...brands, ...criterias];
      const grouped = combinePOI.reduce((acc, item) => {
        const key = item.industry_id;
        if (!acc[key]) {
          acc[key] = {
            industry_id: key,
            category_id: item.category_id,
            category_name: item.category_name,
            category_icon: item.category_icon,
            color: item.color,
            brandCount: 0,
            criteriaCount: 0,
          };
        }
        if (item.brand_id) {
          acc[key].brandCount++;
        }
        if (item.criteria_id) {
          acc[key].criteriaCount++;
        }
        return acc;
      }, {});
      const groupedArray = Object.values(grouped);
      const totalCategories =
        Array.isArray(brands) && Array.isArray(criterias)
          ? new Set([...brands, ...brands].map((item) => item?.category_id))
              .size
          : 0;
      const totalSumBrands = Array.isArray(brands) ? brands.length : 0;
      const totalSumCriteria = Array.isArray(criterias) ? criterias.length : 0;
      setPoiGroup({
        poi_group_list: groupedArray ?? [],
        total_category: totalCategories,
        total_brands: totalSumBrands,
        total_criteria: totalSumCriteria,
      });
    }
  };

  // USEEFFECT
  useEffect(() => {
    if (
      data?.checkedState &&
      JSON.stringify(data.checkedState) !== JSON.stringify(checkedState) &&
      Object.keys(data?.checkedState).length
    ) {
      setCheckedState(data.checkedState);
      if (
        Array.isArray(data?.poi_brand) &&
        JSON.stringify(data.poi_brand) !== JSON.stringify(brands)
      ) {
        setBrands(data.poi_brand);
      }

      if (
        Array.isArray(data?.poi_criteria) &&
        JSON.stringify(data.poi_criteria) !== JSON.stringify(criterias)
      ) {
        setCriterias(data.poi_criteria);
      }
    }
  }, [data?.checkedState, data?.poi_brand, data?.poi_criteria]);
  useEffect(() => {
    onCheckedStateParameterPoi({
      poi_brand: brands,
      poi_criteria: criterias,
      checkedState: checkedState,
      poi_group: poiGroup,
    });
  }, [brands, criterias, checkedState, poiGroup]);
  useEffect(() => {
    onValidOrInvalid(
      (data?.checkedState && Object.keys(data?.checkedState).length > 0) ||
        data?.poi_brand?.length > 0 ||
        data?.poi_criteria?.length > 0
        ? true
        : false
    );
  }, [data, onValidOrInvalid]);
  useEffect(() => {
    groupPoi();
  }, [brands, criterias]);

  return (
    <div className='DatexPoiAdjStepOne'>
      {/* SELECT AREA */}
      <div className='DatexPoiAdjStepOne__container_selectarea'>
        <button
          className={`DatexPoiAdjStepOne__wrapper_selectarea`}
          onClick={handleClickOpenArea}
          style={{
            borderBottomLeftRadius: `${openAreaSelection ? 0 : ''}`,
            borderBottomRightRadius: `${openAreaSelection ? 0 : ''}`,
          }}
        >
          <div
            className={`select-area ${
              openAreaSelection ? 'icon-collapse' : ''
            }`}
          >
            <span className='title-selectarea'>Select Area</span>
            <DynamicIcon height='0.875rem' icon='arrow' width='0.875rem' />
          </div>
        </button>
        {openAreaSelection && (
          <div className='DatexPoiAdjStepOne__content_selectarea'>
            {/* PROVINCE */}
            <div className='select-area-option'>
              <SelectOption
                disabled={loadingProvince}
                dropdownRender={renderProvinceDropdown}
                loading={loadingProvince}
                onChange={(e, obj) => {
                  const selectedProvince = provinceList?.length
                    ? find(provinceList, (v) => v.value === e)
                    : undefined;
                  onChange({
                    province_id: e,
                    province_code: selectedProvince?.province_code,
                    province_name: obj?.children,
                    city_name: undefined,
                    city_id: undefined,
                    city_code: undefined,
                    district_id: undefined,
                    district_code: undefined,
                    district_name: undefined,
                  });
                  debouncedSearchCity('', e);
                  setInputSearch((prev) => ({ ...prev, city: '' }));
                }}
                options={provinceList || []}
                placeholder='Select Province'
                suffixIcon={<IconChevron />}
                value={data?.province_id}
              />
            </div>
            {/* CITY */}
            <div
              className={`select-area-option ${
                data?.province_id ? '' : 'hidden-option'
              }`}
            >
              <SelectOption
                disabled={!data?.province_id || loadingCity}
                dropdownRender={renderCityDropdown}
                loading={loadingCity}
                onChange={(e, obj) => {
                  const selectCity = cityList?.length
                    ? find(cityList, (v) => v.value === e)
                    : undefined;
                  onChange({
                    province_id: data?.province_id,
                    province_code: data.province_code,
                    province_name: data.province_name,
                    city_id: e,
                    city_code: selectCity?.city_code,
                    city_name: obj?.children,
                    district_id: undefined,
                    district_code: undefined,
                    district_name: undefined,
                  });
                  debouncedSearchDistrict('', e);
                  setInputSearch((prev) => ({ ...prev, district: '' }));
                }}
                options={cityList ?? []}
                placeholder='Select City'
                suffixIcon={<IconChevron />}
                value={data?.city_id}
              />
            </div>
            {/* DISTRICT */}
            <div
              className={`select-area-option ${
                data?.province_id && data?.city_id ? '' : 'hidden-option'
              }`}
            >
              <SelectOption
                disabled={!data.city_id || loadingDistrict}
                dropdownRender={renderDistrictDropdown}
                loading={loadingDistrict}
                onChange={(e, obj) => {
                  const selectDistrict = districtList?.length
                    ? find(districtList, (v) => v.value === e)
                    : undefined;
                  onChange({
                    province_id: data?.province_id,
                    province_code: data?.province_code,
                    province_name: data?.province_name,
                    city_id: data?.city_id,
                    city_code: data?.city_code,
                    city_name: data?.city_name,
                    district_id: e,
                    district_code: selectDistrict?.district_code,
                    district_name: obj?.children,
                    village_id: undefined,
                    village_code: undefined,
                    village_name: undefined,
                  });
                  debouncedSearchVillage('', e);
                  setInputSearch((prev) => ({ ...prev, village: '' }));
                }}
                options={districtList || []}
                placeholder='Select District'
                suffixIcon={<IconChevron />}
                value={data?.district_id}
              />
            </div>
            {/* VILLAGE */}
            <div
              className={`select-area-option ${
                data?.province_id && data?.city_id && data?.district_id
                  ? ''
                  : 'hidden-option'
              }`}
            >
              <SelectOption
                disabled={!data.district_id || loadingVillage}
                dropdownRender={renderVillageDropdown}
                loading={loadingVillage}
                onChange={(e, obj) => {
                  const selectDistrict = villageList?.length
                    ? find(villageList, (v) => v.value === e)
                    : undefined;
                  onChange({
                    province_id: data?.province_id,
                    province_code: data?.province_code,
                    province_name: data?.province_name,
                    city_id: data?.city_id,
                    city_code: data?.city_code,
                    city_name: data?.city_name,
                    district_id: data?.district_id,
                    district_code: data?.district_code,
                    district_name: data?.district_name,
                    village_id: e,
                    village_code: selectDistrict?.village_code,
                    village_name: obj?.children,
                  });
                }}
                options={villageList || []}
                placeholder='Village'
                suffixIcon={<IconChevron />}
                value={data?.village_id}
              />
            </div>
          </div>
        )}
      </div>
      {/* POI SELECT */}
      <div className='DatexPoiAdjStepOne__container_poi'>
        <ParameterPoi
          checkedState={data?.checkedState}
          dataListPoi={dataListPoi}
          feat='datex_poi'
          setBrands={setBrands}
          setCheckedState={setCheckedState}
          setCriterias={setCriterias}
          styleHeader={{
            background: '#EBEFF5',
            padding: '0.5rem',
            flexWrap: 'nowrap',
            gap: '0.5rem',
          }}
          widthSearch={100}
          widthSelect={'150px'}
          withButtonText={false}
        />
      </div>
    </div>
  );
};
